// DeepExplain.jsx
import React, { useState, useRef } from 'react';
import { Copy, CheckCircle2, HelpCircle } from 'lucide-react';
import BuyMeACoffee from './coffee';
import NavigationPanel from './NavigationPanel';

const DeepExplain = () => {
  const [inputText, setInputText] = useState('');
  const [explanation, setExplanation] = useState('');
  const [darkMode, setDarkMode] = useState(true);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  /**
   * Fetch explanation by calling the serverless function
   */
  const fetchExplanation = async (selectedText, fullPolicy) => {
    if (!selectedText.trim()) return;

    setExplanation('Loading explanation...');

    try {
      console.log('Fetching explanation for:', selectedText);
      const response = await fetch('/api/explain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ selectedText, fullPolicy }),
      });

      const data = await response.json();
      console.log('Response from OpenAI via serverless function:', data);

      if (data.choices && data.choices[0]?.message?.content) {
        setExplanation(data.choices[0].message.content);
      } else {
        setExplanation('Failed to fetch explanation. Please try again.');
      }
    } catch (error) {
      console.error('Error fetching explanation:', error);
      setExplanation('Failed to fetch explanation. Please try again.');
    }
  };

  /**
   * Handle the "Explain" button click
   */
  const handleExplainClick = () => {
    const selectedText = window.getSelection().toString();
    if (selectedText) {
      fetchExplanation(selectedText, inputText);
    } else {
      setExplanation('Please select some text to explain.');
    }
  };

  /**
   * Copy input text to clipboard
   */
  const copyToClipboard = () => {
    if (!inputText) return;
    try {
      const textarea = document.createElement('textarea');
      textarea.value = inputText;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.top = '50%';
      textarea.style.left = '50%';
      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textarea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error('Failed to copy - please try selecting and copying manually');
      }
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  return (
    <>
      {/* Embedded style for placeholder when the div is empty */}
      <style>
        {`
          .deepexplain-input:empty::before {
            content: "Paste your AWS policy here and highlight any section to receive a clear, insightful explanation...";
            color: #999;
            pointer-events: none;
          }
        `}
      </style>

      <NavigationPanel darkMode={darkMode} setDarkMode={setDarkMode} />

      <div
        className={`
          min-h-screen p-4 md:p-8 transition-colors
          ${darkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}
        `}
      >
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div
              className={`
                md:col-span-2
                rounded-lg shadow p-8 space-y-4 border border-transparent
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
                min-h-[600px]
              `}
            >
              <div className="flex items-center justify-between">
                <label className="text-lg font-semibold">Input Policy</label>
                <div className="flex items-center gap-2">
                  <button
                    onClick={copyToClipboard}
                    className="
                      inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors
                      bg-[#FF9900] text-white hover:bg-[#FFB84D]
                    "
                  >
                    {copied ? (
                      <>
                        <CheckCircle2 size={16} />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy size={16} />
                        Copy Text
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleExplainClick}
                    className="
                      inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition-colors
                      bg-[#FF9900] text-white hover:bg-[#FFB84D]
                    "
                  >
                    <HelpCircle size={16} />
                    Explain
                  </button>
                </div>
              </div>

              {/* Input field for the policy */}
              <div
                ref={inputRef}
                contentEditable
                spellCheck="false"
                onInput={(e) => setInputText(e.currentTarget.innerText)}
                className={`
                  deepexplain-input
                  w-full h-[650px] p-3 border border-amber-200 rounded-md font-mono text-sm
                  focus:outline-none resize-none transition-colors
                  scrollbar-thin
                  scrollbar-thumb-gray-500
                  scrollbar-track-gray-300
                  hover:scrollbar-thumb-gray-600
                  ${darkMode
                    ? 'bg-gray-700 text-gray-100 border-gray-600 scrollbar-track-gray-700 scrollbar-thumb-gray-500 hover:scrollbar-thumb-gray-400'
                    : 'bg-gray-200 text-gray-800 border-gray-300'
                  }
                  whitespace-pre-wrap overflow-auto
                `}
              />
            </div>

            <div
              className={`
                md:col-span-1
                rounded-lg shadow p-8 space-y-4 border border-transparent
                ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50'}
                min-h-[600px]
              `}
            >
              <h2 className="text-lg font-semibold">Explanation</h2>
              <div
                className={`
                  p-4 rounded-md
                  ${darkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-200 text-gray-800'}
                `}
              >
                {explanation || 'Select part of the policy and click "Explain" to see the explanation here.'}
              </div>

              <h2 className="text-lg font-semibold mt-6">How It Works</h2>
              <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Paste your policy in the input field on the left. Select any part of the policy and click
                "Explain" to fetch and display its explanation in the context of the entire policy.
              </p>
              <p
                className={`text-sm mt-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}
              >
                You can also manually select the text and press{' '}
                <kbd
                  className="border bg-gray-100 px-1 text-xs font-mono rounded
                             dark:bg-gray-700 dark:border-gray-500"
                >
                  Ctrl + C
                </kbd>{' '}
                (or Cmd + C on Mac) to copy.
              </p>
            </div>
          </div>
        </div>
      </div>

      <BuyMeACoffee />
    </>
  );
};

export default DeepExplain;